import { GREEN, YELLOW } from '../Theme/palette';
import { BellOutlined, FieldTimeOutlined, InfoCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { L } from '@lib';
import {
  LocalizedServiceOrderStatus,
  LocalizedServiceOrderStatusObjectKey,
  OrderStatus,
  ServiceOrderStatusColor,
  ServiceOrderStatusColorObjectKey,
  IDate,
} from '@models';
import { NotificationService } from '@services';
import { formatDateWithOffset, formatLoggedTime, isOffCalendar, isTimeTbc } from '@utils';
import { Button, ConfigProvider, InputNumber, Popconfirm, Segmented, Tooltip, message } from 'antd';
import { segmentedTheme } from '../styles/shared';
import { SharedCard } from './SharedCard';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { useStore } from '@stores';
import { useRoleAccess, useView } from '@hooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTimeSelect } from '../Form/DateTimeSelect';
import { ServiceHistoryModal } from '../Form/ServiceHistoryModal';
import { OrderStatusSegmented } from '../Select';
import { MasterBuildTag } from '../Tag/MasterBuildTag';
import { useElementWidth } from './useElementWidth';
import { AvailableWorkStationSelect } from '../Select/AvailableWorkStationSelect';
import { TechnicianSelect } from '../Select/TechnicianSelect';

// --- Styles ---

const iconStyle = (color: string): React.CSSProperties => ({
  fontSize: 13,
  color,
  flexShrink: 0,
});

const valueStyle: React.CSSProperties = { fontSize: 13, color: 'rgba(0, 0, 0, 0.88)' };
const emptyValueStyle: React.CSSProperties = { fontSize: 13, color: 'rgba(0, 0, 0, 0.45)' };

// --- Sub-components ---

// The booking drawer's label, so a field reads the same here as when it was booked: small, grey,
// capitals, in a column to the left of its control.
const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#8c8c8c',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  flexShrink: 0,
};

/** One label/control row of the card's grid (the label above its control on a phone). */
const Field = ({
  text,
  hint,
  children,
}: {
  text: string;
  /** Explains the field; shown when hovering the info icon beside the label. */
  hint?: string;
  children: React.ReactNode;
}) => (
  <>
    <span style={fieldLabelStyle}>
      {text}
      {hint && (
        <Tooltip title={hint}>
          <InfoCircleOutlined style={{ marginLeft: 6, color: '#8c8c8c', cursor: 'help' }} />
        </Tooltip>
      )}
    </span>
    <div style={{ minWidth: 0 }}>{children}</div>
  </>
);

const ReadOnlyValue = ({ value, fallback }: { value?: string | null; fallback: string }) => (
  <span style={value ? valueStyle : emptyValueStyle}>{value || fallback}</span>
);

// Read-only display of the time on the job's stopwatch. Counts up
// live while the clock is running, otherwise shows the frozen accumulated total.
const LoggedTimeValue = ({
  accumulatedMinutes,
  startedAt,
  running,
}: {
  accumulatedMinutes: number;
  startedAt?: string;
  running: boolean;
}) => {
  const startedMs = running && startedAt ? dayjs(startedAt).valueOf() : null;
  const compute = () =>
    accumulatedMinutes + (startedMs ? Math.max(0, (Date.now() - startedMs) / 60000) : 0);
  const [minutes, setMinutes] = useState(compute);

  useEffect(() => {
    setMinutes(compute());
    if (!startedMs) return;
    const id = setInterval(() => setMinutes(compute()), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedMs, accumulatedMinutes]);

  return <ReadOnlyValue value={formatLoggedTime(minutes)} fallback="—" />;
};

// The build this job is a leg of, for the drawer title / page header - it is one tag that never
// changes while the job is open, so it does not need a field of its own on the card.
export const JobBuildTag = observer(() => {
  const { order } = useStore().orderStore;

  if (!order?.masterBuildId) return null;

  return <MasterBuildTag order={order} />;
});

const buttonTheme = (main: string, hover: string, active: string) => ({
  components: { Button: { colorPrimary: main, colorPrimaryHover: hover, colorPrimaryActive: active } },
});
// The running-badge green (see RUNNING_GREEN in the calendar card) and the on-hold amber.
const START_BUTTON_THEME = buttonTheme(GREEN.tag, GREEN.badge, GREEN.active);
const PAUSE_BUTTON_THEME = buttonTheme(YELLOW.tag, YELLOW.badge, YELLOW.active);

// The three buttons beside the stopwatch (start / pause / resume, complete) are one height: a little
// under the form controls', so the heading row stays light.
const timerButtonStyle: React.CSSProperties = { height: 28, padding: '0 12px', fontSize: 13 };

// The job's stopwatch, for the "Job card details" heading: how long the job has actually been worked
// on. The button beside it starts it (Booked or On hold -> In progress) and pauses it (-> On hold). Away
// from the work log button on purpose: that one is hand-logged, per-technician time, and the two
// differ. The clock is saved by the button itself, not by Save.
export const JobLoggedTime = observer(() => {
  const { orderStore } = useStore();
  const { order } = orderStore;
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);

  if (!order) return null;

  // The clock runs while the job is In progress: Start moves a Booked (or On hold) job there, Pause
  // puts it On hold. Judged
  // on the status the server has, not the select's: a status picked there but not saved yet is not
  // one the job is in, and Start on it would be refused.
  const savedStatus = orderStore.savedStatus ?? order.status;
  const running = savedStatus === OrderStatus.InProgress;
  // Work starts only on a Booked or On hold job that is on the calendar - a date, a time and
  // somebody to do it. Pausing is always allowed.
  const startBlocked = order.status !== savedStatus
    ? 'Save the status change first - the timer starts from the job as it is saved.'
    : savedStatus !== OrderStatus.Booked && savedStatus !== OrderStatus.OnHold
    ? 'The timer can only be started while the job is Booked or On hold.'
    : isOffCalendar(order)
      ? 'The timer can only be started once the job has a date, a time and a technician or workstation.'
      : undefined;

  // Finishing the work: the same mileage step as choosing Completed anywhere else, which saves the
  // status - and the server pauses the stopwatch with it, banking what had run.
  const canComplete =
    savedStatus === OrderStatus.Booked ||
    savedStatus === OrderStatus.InProgress ||
    savedStatus === OrderStatus.OnHold;

  const toggle = async () => {
    setBusy(true);
    try {
      await orderStore.setOrderTimer(!running);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
      <Tooltip title="Stopwatch: how long the job has been worked on. Technicians' own hours are in the work log.">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FieldTimeOutlined style={iconStyle(GREEN.tag)} />
          <span style={{ color: 'rgba(0, 0, 0, 0.65)' }}>Time in progress</span>
          <strong>
            <LoggedTimeValue
              accumulatedMinutes={order.inProgressMinutes ?? 0}
              startedAt={order.inProgressStartedAt}
              running={running}
            />
          </strong>
        </span>
      </Tooltip>
      <Tooltip title={!running ? startBlocked : undefined}>
        <span>
          {/* Start is the green of the running badge on the calendar card, Pause the amber of the
              yellow status group; Complete keeps the primary colour. */}
          <ConfigProvider theme={running ? PAUSE_BUTTON_THEME : START_BUTTON_THEME}>
            <Button
              type="primary"
              style={timerButtonStyle}
              loading={busy}
              disabled={!running && !!startBlocked}
              onClick={toggle}
            >
              {running ? 'Pause job' : savedStatus === OrderStatus.OnHold ? 'Resume job' : 'Start job'}
            </Button>
          </ConfigProvider>
        </span>
      </Tooltip>
      {canComplete && (
        <Button type="primary" style={timerButtonStyle} onClick={() => setCompleting(true)}>
          Complete job
        </Button>
      )}
      {completing && (
        <ServiceHistoryModal
          orderId={order.id}
          customerId={order.customerId}
          vin={order.vin}
          registrationNumber={order.registrationNumber}
          mileage={order.mileage}
          open={completing}
          setOpen={setCompleting}
          onCancel={() => setCompleting(false)}
          // The status change has stopped the clock on the server; this brings its banked time here.
          onCompleted={() => orderStore.setOrderTimer(false)}
        />
      )}
    </span>
  );
});

// The card's width (its grid, label column included) under which it is "narrow": about what a
// laptop gives it beside the side panel. The wide layout needs a control column of about 350px.
const NARROW_CARD_WIDTH = 470;

// --- Types ---
export const JobDetailsCard = observer(() => {
  const { orderStore } = useStore();
  const { isMobile } = useView();
  const { order: access } = useRoleAccess();
  const { order } = orderStore;
  const [openMileageModal, setOpenMileageModal] = useState(false);
  const [notifying, setNotifying] = useState(false);
  // On a laptop the card is narrow (the side panel and the other two cards share the row): there the
  // buttons beside the date pickers are icons only - see `narrow` below.
  const { ref: gridRef, width: gridWidth } = useElementWidth<HTMLDivElement>();

  if (!order) return null;

  const isLocked = order.status === OrderStatus.Paid || order.status === OrderStatus.Ready;

  const updateField = useCallback(
    (field: string, value: any) => {
      orderStore.updateOrderField({ [field]: value });
    },
    [orderStore]
  );

  // --- Job date and drop-off ---

  // The job is picked by its start; its length stays, so the end moves with it (the card no longer
  // asks for an end - the services say how long the job is).
  const handleJobStartChange = useCallback(
    (value: IDate) => {
      if (!value.startAt) return;
      const length =
        order.startAt && order.endAt ? dayjs(order.endAt).diff(dayjs(order.startAt), 'minute') : 0;
      const start = dayjs(value.startAt);
      orderStore.updateOrderField({
        startAt: formatDateWithOffset(start),
        endAt: formatDateWithOffset(start.add(length > 0 ? length : 60, 'minute')),
      });
    },
    [order.startAt, order.endAt, orderStore]
  );

  const handleDropOffChange = useCallback(
    (value: IDate) => {
      if (value.startAt) updateField('dropOffDate', formatDateWithOffset(dayjs(value.startAt)));
    },
    [updateField]
  );

  // --- Drop-off / job-date consistency warning ---

  // Drop-off is expected to fall on the same day as the job. When the job date is moved
  // (forward or backward), the drop-off can land on a different day and needs re-syncing.
  // The check compares the drop-off against the job date only (not "now"). We only warn
  // (nothing is blocked or required) and offer a one-click sync.
  const dropOffOutOfSync = useMemo(() => {
    if (!order.startAt || !order.dropOffDate) return false;
    return !dayjs(order.dropOffDate).isSame(dayjs(order.startAt), 'day');
  }, [order.startAt, order.dropOffDate]);

  // Aligns the drop-off with the job date, keeping its time-of-day but never letting it
  // land after the job start.
  const handleSyncDropOffToJob = useCallback(() => {
    if (!order.startAt) return;
    const jobStart = dayjs(order.startAt);
    const current = order.dropOffDate ? dayjs(order.dropOffDate) : jobStart;
    let synced = jobStart.hour(current.hour()).minute(current.minute()).second(0);
    // A TBC job starts "sometime that day": the drop-off keeps its time rather than being
    // pulled to midnight.
    if (!isTimeTbc(order.startAt) && synced.isAfter(jobStart)) synced = jobStart;
    updateField('dropOffDate', formatDateWithOffset(synced));
  }, [order.startAt, order.dropOffDate, updateField]);

  // --- Handlers ---

  const handleStatusChange = useCallback(
    (status: string) => {
      if (status === OrderStatus.Completed) {
        setOpenMileageModal(true);
      } else {
        updateField('status', status);
      }
    },
    [updateField]
  );

  const handleNotifyCustomer = useCallback(async () => {
    if (!order.customer?.userId) {
      message.warning('Customer is not an app user.');
      return;
    }
    setNotifying(true);
    try {
      await NotificationService.create({
        isInitial: false,
        sendPush: true,
        orderId: order.id,
        dropOffDate: String(order.dropOffDate || ''),
      });
      message.success('Customer notified');
    } catch {
      message.error('Failed to notify customer');
    } finally {
      setNotifying(false);
    }
  }, [order.id, order.customer?.userId, order.dropOffDate]);

  // Telling the customer is an action on the drop-off, so it lives in that field rather than as a
  // bar across the card. It sends whatever is on the order, so an unsaved edit must save first -
  // otherwise the customer is told a date the job does not have.
  const dropOffSaved =
    !order.dropOffDate && !orderStore.savedDropOffDate
      ? true
      : !!order.dropOffDate &&
        !!orderStore.savedDropOffDate &&
        dayjs(order.dropOffDate).isSame(dayjs(orderStore.savedDropOffDate));

  const canNotify = !!order.customer?.userId;
  const notifyEnabled = canNotify && dropOffSaved;

  // Narrow: the card's own width cannot carry a date, a time and a 138px button in one row. Then the
  // buttons beside the pickers are icons only. The phone has its own layout (label above control), so it is
  // not "narrow" in this sense. Not measured yet (0) counts as roomy.
  const narrow = !isMobile && gridWidth > 0 && gridWidth < NARROW_CARD_WIDTH;
  const iconOnly = isMobile || narrow;

  // Buttons the height of the pickers they sit beside, one width so the two line up (just the icon on a
  // phone or in a narrow card, where there is no room for the words). Always rendered;
  // a customer who is not on the app just sees notify disabled, and the hover says why.
  const iconButtonStyle: React.CSSProperties = {
    width: iconOnly ? 32 : 138,
    height: 32,
    flexShrink: 0,
    padding: '0 8px',
    whiteSpace: 'nowrap',
  };

  // The drop-off does not match the job date: the "Sync" button beside the job date comes alive, with
  // the explanation on hover. It is always there (so the row does not change shape); not live for a
  // completed job - the vehicle has long been dropped off.
  const showSync =
    access.dropOffRow && !isLocked && order.status !== OrderStatus.Completed && dropOffOutOfSync;

  // What the button will do, or why it cannot, on hover: the hint that used to sit under the row.
  // A disabled button swallows hover, so the tooltip is on the span around it.
  const notifyTooltip = !canNotify
    ? 'Only customers on the app can be notified of a drop-off change'
    : !dropOffSaved
      ? 'Save the job first - the customer is told the saved drop-off'
      : 'Notify customer of the drop-off';

  const notifyButton = (
    // Asks first: the push goes out the moment it is confirmed.
    <Popconfirm
      disabled={!notifyEnabled}
      title="Notify the customer?"
      description="A push notification with the drop-off date will be sent to the customer straight away."
      okText="Notify"
      cancelText="Cancel"
      onConfirm={handleNotifyCustomer}
    >
      <Tooltip title={notifyTooltip}>
        <span style={{ display: 'block', flexShrink: 0 }}>
          <Button
            icon={<BellOutlined />}
            disabled={!notifyEnabled}
            loading={notifying}
            style={iconButtonStyle}
          >
            {!iconOnly && 'Notify customer'}
          </Button>
        </span>
      </Tooltip>
    </Popconfirm>
  );

  const syncButton = (
    <Tooltip
      title={
        showSync
          ? 'The drop-off date does not match the job date. Click to set it to the job date.'
          : 'The drop-off is on the job date'
      }
    >
      <span style={{ display: 'block', flexShrink: 0 }}>
        <Button
          icon={<SyncOutlined />}
          disabled={!showSync}
          onClick={handleSyncDropOffToJob}
          style={iconButtonStyle}
        >
          {!iconOnly && 'Sync drop-off'}
        </Button>
      </span>
    </Tooltip>
  );

  // --- Render ---

  const cardGrid: React.CSSProperties = {
    display: 'grid',
    // The booking drawer's own grid: label, then control. On a phone the label sits above its control.
    gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'auto minmax(0, 1fr)',
    alignItems: 'center',
    columnGap: 12,
    rowGap: isMobile ? 6 : 10,
    alignContent: 'start',
  };

  return (
    <SharedCard style={{ height: '100%' }}>
      <div ref={gridRef} style={cardGrid}>
        <Field text="Status">
          {isLocked ? (
            <span
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                background:
                  ServiceOrderStatusColor[order.status as ServiceOrderStatusColorObjectKey] ||
                  '#f0f0f0',
                color: 'rgba(0, 0, 0, 0.75)',
              }}
            >
              {LocalizedServiceOrderStatus[order.status as LocalizedServiceOrderStatusObjectKey] ||
                order.status}
            </span>
          ) : (
            <OrderStatusSegmented
              // A draft only leaves draft by being sent for approval (the footer button), or cancelled.
              disabled={order.status === OrderStatus.Draft}
              status={order.status}
              onChange={handleStatusChange}
            />
          )}
        </Field>

        {/* Whether the car is on site, a row of its own with its label, as in the booking drawer: it
            fits whatever the width. Saved with the card's Save like every other field; the
            API stamps the arrival time then (the time held here until that is only a marker that the
            switch is on). Locked with the rest of the card (Ready for payment, Paid): plain text. */}
        <Field text={L('Car on site')}>
          {!isLocked ? (
            <ConfigProvider theme={segmentedTheme}>
              <Segmented
                block
                value={order.vehicleArrivedAt ? 'Yes' : 'No'}
                onChange={(val) =>
                  updateField('vehicleArrivedAt', val === 'Yes' ? new Date().toISOString() : null)
                }
                options={['No', 'Yes']}
              />
            </ConfigProvider>
          ) : (
            <ReadOnlyValue value={order.vehicleArrivedAt ? 'Yes' : 'No'} fallback="No" />
          )}
        </Field>

        <Field text="Mileage">
          {!isLocked ? (
            <InputNumber
              placeholder="Enter mileage"
              style={{ width: '100%' }}
              controls={false}
              value={order.mileage}
              min={0}
              max={1000000}
              formatter={(val) => (val ? `${val}`.replace(/\./g, '') : '')}
              parser={(val) => (val ? Number(val.replace(/\./g, '')) : '') as any}
              onChange={(val) => updateField('mileage', val ?? undefined)}
            />
          ) : (
            <ReadOnlyValue
              value={order.mileage ? `${order.mileage.toLocaleString()} mls` : undefined}
              fallback="—"
            />
          )}
        </Field>

        {access.dropOffRow && (
          <Field text={L('Drop off')}>
            {!isLocked ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <DateTimeSelect
                    allowPast
                    dateTime={{ startAt: order.dropOffDate }}
                    setDate={handleDropOffChange}
                  />
                </div>
                {notifyButton}
              </div>
            ) : (
              <ReadOnlyValue
                value={
                  order.dropOffDate ? dayjs(order.dropOffDate).format('YYYY-MM-DD, HH:mm') : undefined
                }
                fallback="—"
              />
            )}
          </Field>
        )}

        {/* The same date and start time as when it was booked; no end - the job's length stays. */}
        <Field text={L('Date & time')}>
          {!isLocked ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <DateTimeSelect
                  allowTbc
                  allowPast
                  dateTime={{ startAt: order.startAt }}
                  setDate={handleJobStartChange}
                />
              </div>
              {access.dropOffRow && syncButton}
            </div>
          ) : (
            <ReadOnlyValue
              value={
                !order.startAt
                  ? undefined
                  : isTimeTbc(order.startAt)
                    ? `${dayjs(order.startAt).format('ddd, MMM DD')} · ${L('TBC')}`
                    : `${dayjs(order.startAt).format('ddd, MMM DD')} ${dayjs(order.startAt).format('HH:mm')}`
              }
              fallback={L('No date')}
            />
          )}
        </Field>

        <Field text="Technician">
          {!isLocked ? (
            <TechnicianSelect
              style={{ width: '100%' }}
              placeholder="No technician"
              value={order.userId ? Number(order.userId) : undefined}
              disabled={isLocked}
              defaultValue={
                order.user
                  ? [{ label: order.user.fullname, value: Number(order.userId) }]
                  : undefined
              }
              onClear={() => updateField('userId', undefined)}
              onSelect={(id) => updateField('userId', id)}
              onDeselect={() => updateField('userId', undefined)}
            />
          ) : (
            <ReadOnlyValue value={order.user?.fullname} fallback={L('No technician')} />
          )}
        </Field>

        <Field text="Workstation">
          {!isLocked ? (
            <AvailableWorkStationSelect
              orderId={order.id}
              startAt={order.startAt}
              endAt={order.endAt}
              disabled={isLocked}
              placeholder="No workstation"
              style={{ width: '100%' }}
              value={order.workStationId}
              defaultValue={
                order.workStation
                  ? [{ label: order.workStation.name, value: order.workStationId }]
                  : undefined
              }
              onChange={(val) => updateField('workStationId', val)}
            />
          ) : (
            <ReadOnlyValue value={order.workStation?.name} fallback="No workstation" />
          )}
        </Field>
      </div>

      {/* Mileage modal */}
      {openMileageModal && (
        <ServiceHistoryModal
          orderId={order.id}
          customerId={order.customerId}
          vin={order.vin}
          registrationNumber={order.registrationNumber}
          mileage={order.mileage}
          open={openMileageModal}
          setOpen={setOpenMileageModal}
          onCancel={() => setOpenMileageModal(false)}
          // The status change has stopped the clock on the server; this brings the status and the
          // banked time here, so the buttons beside the stopwatch know where the job is.
          onCompleted={() => orderStore.setOrderTimer(false)}
        />
      )}
    </SharedCard>
  );
});
